<!-- PC 端双摇杆输入组件：支持鼠标/触摸拖动和 Gamepad API 同步。 -->
<template>
  <div class="joystick-overlay" :class="{ disabled: disabled }">
    <!-- 左轮盘：PS4 左摇杆，当前使用 Y 轴控制 throttle -->
    <div
      class="joystick-dial"
      :style="dialStyle"
      @pointerdown.prevent="onPointerDown($event, 'left')"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <div class="dial-glow"></div>
      <div class="dial-grid"></div>
      <div class="dial-arrow dial-arrow--top"></div>
      <div class="dial-arrow dial-arrow--bottom"></div>
      <div class="dial-arrow dial-arrow--left"></div>
      <div class="dial-arrow dial-arrow--right"></div>
      <div class="dial-handle dial-handle--cyan" :style="leftHandleStyle">
        <span class="dial-handle-inner"></span>
      </div>
    </div>

    <!-- 右轮盘：PS4 右摇杆，当前使用 X 轴控制 roll -->
    <div
      class="joystick-dial"
      :style="dialStyle"
      @pointerdown.prevent="onPointerDown($event, 'right')"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
    >
      <div class="dial-glow dial-glow--orange"></div>
      <div class="dial-grid"></div>
      <div class="dial-arrow dial-arrow--top"></div>
      <div class="dial-arrow dial-arrow--bottom"></div>
      <div class="dial-arrow dial-arrow--left"></div>
      <div class="dial-arrow dial-arrow--right"></div>
      <div class="dial-handle dial-handle--orange" :style="rightHandleStyle">
        <span class="dial-handle-inner"></span>
      </div>
    </div>
  </div>
</template>

<script>
// Copyright (c) 2026 Rotunbot, Inc. All rights reserved.
// 虚拟摇杆组件：鼠标/触摸拖动 + Gamepad API 同步

const GAMEPAD_DEAD_ZONE = 0.12;

export default {
  name: 'JoystickControl',
  props: {
    disabled: { type: Boolean, default: false },
    size: { type: Number, default: 150 },
    // 是否启用手柄视觉同步（遥控开启后才同步）
    enableGamepadVisual: { type: Boolean, default: false }
  },
  data() {
    return {
      // 左轮盘归一化坐标 [-1,1]
      leftX: 0,
      leftY: 0,
      // 右轮盘归一化坐标 [-1,1]
      rightX: 0,
      rightY: 0,

      isDragging: false,
      activeJoystick: null, // 'left' | 'right'

      gamepadIndex: null,
      gamepadConnected: false,
      gamepadRaf: null,
      // 记录手柄是否有移动（用于未开启遥控时的提示）
      gamepadHasMovement: false,
      // 上次手柄轴值（用于检测移动）
      lastGamepadAxes: null
    };
  },
  computed: {
    dialStyle() {
      return { width: this.size + 'px', height: this.size + 'px' };
    },
    // 协议值映射：yaw/roll/pitch 为 [-1000, 1000] 中位 0；
    // throttle 为 [0, 1000] 中位 500。
    mappedYaw() {
      // 图 1：yaw 当前预留，固定中位值。
      return 0;
    },
    mappedThrottle() {
      // 屏幕 Y 向上为负，协议 throttle 向上为正。
      return Math.round(500 - this.leftY * 500);
    },
    mappedRoll() {
      return Math.round(this.rightX * 1000);
    },
    mappedPitch() {
      // 图 1：pitch 当前预留，固定中位值。
      return 0;
    },
    leftHandleStyle() {
      const r = this.size * 0.32;
      return {
        transform: `translate(-50%, -50%) translate(${this.leftX * r}px, ${this.leftY * r}px)`
      };
    },
    rightHandleStyle() {
      const r = this.size * 0.32;
      return {
        transform: `translate(-50%, -50%) translate(${this.rightX * r}px, ${this.rightY * r}px)`
      };
    }
  },
  watch: {
    disabled(value) {
      if (value) this.resetAxes(false);
    }
  },
  mounted() {
    window.addEventListener('gamepadconnected', this.onGamepadConnected);
    window.addEventListener('gamepaddisconnected', this.onGamepadDisconnected);
    this.startGamepadPolling();
  },
  beforeDestroy() {
    window.removeEventListener('gamepadconnected', this.onGamepadConnected);
    window.removeEventListener('gamepaddisconnected', this.onGamepadDisconnected);
    this.stopGamepadPolling();
  },
  methods: {
    // 向父组件通知手柄连接或断开状态。
    announceGamepadState(connected, gamepad) {
      this.gamepadConnected = connected;
      this.$emit('gamepad', {
        connected,
        id: gamepad && gamepad.id ? gamepad.id : ''
      });
    },

    /* ---------- 鼠标/触摸拖动 ---------- */
    // 开始拖动指定轮盘并捕获指针。
    onPointerDown(e, joystick) {
      if (this.disabled) return;
      this.isDragging = true;
      this.activeJoystick = joystick;
      if (e.currentTarget.setPointerCapture) {
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
      }
      this.updateFromPointer(e, joystick);
    },
    // 更新当前拖动轮盘的位置。
    onPointerMove(e) {
      if (!this.isDragging || !this.activeJoystick) return;
      this.updateFromPointer(e, this.activeJoystick);
    },
    // 将指针坐标转换为轮盘归一化轴值。
    updateFromPointer(e, joystick) {
      const rect = e.currentTarget.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      let x = (e.clientX - centerX) / (rect.width / 2);
      let y = (e.clientY - centerY) / (rect.height / 2);

      // 限制在单位圆内
      const len = Math.sqrt(x * x + y * y);
      if (len > 1) { x = x / len; y = y / len; }

      if (joystick === 'left') {
        this.leftX = x;
        this.leftY = y;
      } else {
        this.rightX = x;
        this.rightY = y;
      }
      this.emitChange();
    },
    // 松手后让当前轮盘回到中位。
    onPointerUp() {
      if (!this.activeJoystick) return;
      const joystick = this.activeJoystick;
      this.isDragging = false;
      this.activeJoystick = null;

      // 松手自动回中
      if (joystick === 'left') {
        this.leftX = 0;
        this.leftY = 0;
      } else {
        this.rightX = 0;
        this.rightY = 0;
      }
      this.emitChange();
    },

    /* ---------- Gamepad 同步 ---------- */
    // 记录新连接的 Gamepad 索引。
    onGamepadConnected(e) {
      this.gamepadIndex = e.gamepad.index;
      this.announceGamepadState(true, e.gamepad);
    },
    // 处理手柄断开并将输入安全回中。
    onGamepadDisconnected(e) {
      if (e && this.gamepadIndex !== e.gamepad.index) return;
      this.gamepadIndex = null;
      this.announceGamepadState(false);
      // 手柄断开时回中，保证安全
      this.resetAxes(!this.disabled);
    },
    // 死区处理 + 线性映射回 [-1,1]
    applyDeadZone(v) {
      if (Math.abs(v) < GAMEPAD_DEAD_ZONE) return 0;
      return (v - Math.sign(v) * GAMEPAD_DEAD_ZONE) / (1 - GAMEPAD_DEAD_ZONE);
    },
    // 启动 requestAnimationFrame 轮询，持续同步手柄轴值。
    startGamepadPolling() {
      const poll = () => {
        const pads = navigator.getGamepads ? navigator.getGamepads() : [];
        if (this.gamepadIndex == null) {
          const first = Array.from(pads).find((item) => item);
          if (first) {
            this.gamepadIndex = first.index;
            // 面板打开前已经插入手柄时，gamepadconnected 事件可能不会再次触发。
            this.announceGamepadState(true, first);
          }
        }
        const pad = this.gamepadIndex != null ? pads[this.gamepadIndex] : null;
        if (this.gamepadIndex != null && !pad) {
          this.gamepadIndex = null;
          this.announceGamepadState(false);
          this.resetAxes(!this.disabled);
          this.gamepadHasMovement = false;
          this.lastGamepadAxes = null;
        }
        // 手柄始终同步到页面轮盘；disabled 只禁止鼠标/触摸操作和实际发送，
        // 不影响轮盘的可视化反馈。这样未开始遥控时也能确认手柄轴值是否被浏览器识别。
        if (pad) {
          const lx = this.applyDeadZone(pad.axes[0] || 0);
          const ly = this.applyDeadZone(pad.axes[1] || 0);
          const rx = this.applyDeadZone(pad.axes[2] || 0);
          const ry = this.applyDeadZone(pad.axes[3] || 0);

          // 检测手柄是否有移动
          const hasMovement = (
            Math.abs(lx) > 0.01 || Math.abs(ly) > 0.01 ||
            Math.abs(rx) > 0.01 || Math.abs(ry) > 0.01
          );

          // 未开启遥控时，检测到手柄移动则发出提示
          if (!this.enableGamepadVisual && hasMovement && !this.gamepadHasMovement) {
            this.gamepadHasMovement = true;
            this.$emit('gamepad-movement', true);
          } else if (!hasMovement && this.gamepadHasMovement) {
            this.gamepadHasMovement = false;
            this.$emit('gamepad-movement', false);
          }

          // 只有在开启遥控后才同步到画面
          if (this.enableGamepadVisual) {
            if (
              lx !== this.leftX || ly !== this.leftY ||
              rx !== this.rightX || ry !== this.rightY
            ) {
              // PS4 标准布局：axes[0]/[1] 为左摇杆，axes[2]/[3] 为右摇杆。
              this.leftX = lx;
              this.leftY = ly;
              this.rightX = rx;
              this.rightY = ry;
              this.emitChange();
            }
          }
        }
        this.gamepadRaf = requestAnimationFrame(poll);
      };
      this.gamepadRaf = requestAnimationFrame(poll);
    },
    // 停止 Gamepad 轮询，避免组件销毁后继续占用帧循环。
    stopGamepadPolling() {
      if (this.gamepadRaf) {
        cancelAnimationFrame(this.gamepadRaf);
        this.gamepadRaf = null;
      }
    },

    // 清零两个轮盘并按需通知父组件。
    resetAxes(emit = true) {
      this.isDragging = false;
      this.activeJoystick = null;
      this.leftX = 0;
      this.leftY = 0;
      this.rightX = 0;
      this.rightY = 0;
      if (emit) this.emitChange();
    },

    emitChange() {
      this.$emit('change', {
        yaw: this.mappedYaw,
        throttle: this.mappedThrottle,
        roll: this.mappedRoll,
        pitch: this.mappedPitch
      });
    }
  }
};
</script>

<style scoped>
.joystick-overlay {
  position: absolute;
  bottom: 45px;   /* 播放器底部工具栏约 48px，额外留 30px 安全间距 */
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  padding: 0 16px;
  z-index: 10002;
  isolation: isolate;
  transform: translateZ(0);
  background: transparent;
  user-select: none;
  pointer-events: none; /* 镂空区域不挡视频 */
}
.joystick-overlay > * {
  pointer-events: auto;
}
.joystick-overlay.disabled {
  opacity: 0.4;
  pointer-events: none;
}

/* ---- 轮盘底盘 ---- */
.joystick-dial {
  position: relative;
  border-radius: 50%;
  cursor: pointer;
  touch-action: none;
  /* 扁平深色底盘 + 微弱内阴影 */
  background: radial-gradient(circle at 50% 42%, rgba(255,255,255,0.07), rgba(10,18,26,0.55) 75%);
  border: 1px solid rgba(255, 255, 255, 0.16);
  box-shadow:
    inset 0 2px 12px rgba(0, 0, 0, 0.55),
    0 4px 16px rgba(0, 0, 0, 0.35);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.joystick-dial:active {
  border-color: rgba(0, 229, 255, 0.6);
  box-shadow:
    inset 0 2px 12px rgba(0, 0, 0, 0.55),
    0 0 18px rgba(0, 229, 255, 0.35);
}

/* 中央微光 */
.dial-glow {
  position: absolute;
  inset: 30%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 229, 255, 0.14), transparent 70%);
  pointer-events: none;
}
.dial-glow--orange {
  background: radial-gradient(circle, rgba(255, 152, 0, 0.14), transparent 70%);
}

/* 背景网格点（科技感细节） */
.dial-grid {
  position: absolute;
  inset: 12%;
  border-radius: 50%;
  background-image:
    radial-gradient(circle, rgba(255,255,255,0.13) 1px, transparent 1px);
  background-size: 12px 12px;
  background-position: center;
  opacity: 0.5;
  pointer-events: none;
  mask-image: radial-gradient(circle, black 40%, transparent 75%);
  -webkit-mask-image: radial-gradient(circle, black 40%, transparent 75%);
}

/* 四向箭头指示 */
.dial-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-bottom: 5px solid rgba(255, 255, 255, 0.35);
  pointer-events: none;
}
.dial-arrow--top {
  top: 6%;
  left: 50%;
  transform: translateX(-50%);
}
.dial-arrow--bottom {
  bottom: 6%;
  left: 50%;
  transform: translateX(-50%) rotate(180deg);
}
.dial-arrow--left {
  top: 50%;
  left: 6%;
  transform: translateY(-50%) rotate(-90deg);
}
.dial-arrow--right {
  top: 50%;
  right: 6%;
  transform: translateY(-50%) rotate(90deg);
}

/* ---- 摇杆头 ---- */
.dial-handle {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 30%;
  height: 30%;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: transform 0.06s linear;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  /* 半透明磨砂感 */
  background: rgba(30, 38, 48, 0.75);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.5),
    inset 0 1px 2px rgba(255, 255, 255, 0.25);
}
.dial-handle-inner {
  width: 38%;
  height: 38%;
  border-radius: 50%;
}
.dial-handle--cyan .dial-handle-inner {
  background: radial-gradient(circle at 35% 30%, #8df6ff, #00cfe6 70%);
  box-shadow: 0 0 8px rgba(0, 229, 255, 0.8);
}
.dial-handle--orange .dial-handle-inner {
  background: radial-gradient(circle at 35% 30%, #ffd79a, #ff9800 70%);
  box-shadow: 0 0 8px rgba(255, 152, 0, 0.8);
}

</style>
